import { getStandardButtonStyles } from "../../styles/standardButtons";
import React, { useState, useEffect, useRef, useContext } from "react";

import * as Yup from "yup";
import { Formik, FieldArray, Form, Field } from "formik";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import Typography from "@material-ui/core/Typography";
import CircularProgress from "@material-ui/core/CircularProgress";

import { i18n } from "../../translate/i18n";

import api, { getMediaUrl } from "../../services/api";
import toastError from "../../errors/toastError";
import { Stack } from "@mui/material";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles(theme => ({
  ...getStandardButtonStyles(theme),
    root: {
        display: "flex",
        flexWrap: "wrap"
    },
    textField: {
        marginRight: theme.spacing(1),
        flex: 1
    },

    extraAttr: {
        display: "flex",
        justifyContent: "center",
        alignItems: "center"
    },

    btnWrapper: {
        position: "relative"
    },

    buttonProgress: {
        color: green[500],
        position: "absolute",
        top: "50%",
        left: "50%",
        marginTop: -12,
        marginLeft: -12
    }
}));

const FlowBuilderAddDocModal = ({ open, onSave, onUpdate, data, close }) => {
    const classes = useStyles();
    const isMounted = useRef(true);

    const { user } = useContext(AuthContext);
    const companyId = user?.companyId;
    const link = process.env.REACT_APP_BACKEND_URL === 'http://localhost:8090' ? 'http://localhost:8090' : process.env.REACT_APP_BACKEND_URL;

    const [activeModal, setActiveModal] = useState(false);
    const [loading, setLoading] = useState(false);
    const [medias, setMedias] = useState([]);

    const [labels, setLabels] = useState({
        title: i18n.t("flowBuilder.doc.addTitle") || "Adicionar documento",
        btn: i18n.t("common.add") || "Adicionar"
    });

    const [name, setName] = useState("");
    const [caption, setCaption] = useState("");

    useEffect(() => {
        if (open === "edit") {
            setLabels({
                title: i18n.t("flowBuilder.doc.editTitle") || "Editar documento",
                btn: i18n.t("common.save")
            });
            setName(data.data.name || "");
            setCaption(data.data.caption || "");
            setActiveModal(true);
        } else if (open === "create") {
            setLabels({
                title: i18n.t("flowBuilder.doc.addTitle") || "Adicionar documento",
                btn: i18n.t("common.add") || "Adicionar"
            });
            setName("");
            setCaption("");
            setActiveModal(true);
        } else {
            setActiveModal(false);
        }
    }, [open]);

    useEffect(() => {
        return () => {
            isMounted.current = false;
        };
    }, []);

    const handleClose = () => {
        close(null);
        setActiveModal(false);
        setMedias([]);
    };

    const handleSaveContact = async () => {
        if (open === "edit" && medias.length === 0) {
            handleClose();
            onUpdate({
                ...data,
                data: {
                    ...data.data,
                    name: name,
                    caption: caption,
                    url: data.data.url
                }
            });
            return;
        }

        setLoading(true);
        const formData = new FormData();
        formData.append("fromMe", true);
        formData.append("typeArch", "flow");

        medias.forEach((media) => {
            formData.append("medias", media);
            formData.append("body", media.name);
        });

        try {
            const { data: resData } = await api.post("/flowbuilder/img", formData); // Assuming same endpoint for docs as distinct from generic? Original used /img for doc creation too? Wait.
            // In original code: const { data } = await api.post("/flowbuilder/img", formData); 
            // Yes, it used /img endpoint.

            handleClose();

            if (open === "edit") {
                onUpdate({
                    ...data,
                    data: {
                        ...data.data,
                        name: name,
                        caption: caption,
                        url: resData.name ? resData.name.split(/[/\\]/).pop() : "",
                        original: medias[0]?.name || data.data.original || resData.originalName || resData.name
                    }
                });
            } else {
                onSave({
                    url: resData.name ? resData.name.split(/[/\\]/).pop() : "",
                    name: name,
                    caption: caption,
                    original: medias[0]?.name || resData.originalName || resData.name
                });
            }

            toast.success(i18n.t("flowBuilder.doc.success") || "Documento adicionado com sucesso!");
            setMedias([]);
            setName("");
            setCaption("");
        } catch (err) {
            toastError(err);
        } finally {
            setLoading(false);
        }
    };

    const handleChangeMedias = e => {
        if (!e.target.files) {
            return;
        }

        // Limit to 20MB for docs
        if (e.target.files[0].size > 20000000) {
            toast.error(i18n.t("flowBuilder.video.tooLarge") || "Arquivo é muito grande! 20MB máximo");
            return;
        }

        const selectedMedias = Array.from(e.target.files);
        setMedias(selectedMedias);
    };

    return (
        <div className={classes.root}>
            <Dialog
                open={activeModal}
                onClose={handleClose}
                fullWidth="md"
                scroll="paper"
            >
                <DialogTitle id="form-dialog-title">{labels.title}</DialogTitle>
                <Stack>
                    <DialogContent dividers>
                        <Stack gap={"16px"}>
                            <TextField
                                label={i18n.t("flowBuilder.form.name") || "Nome"}
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                fullWidth
                                variant="outlined"
                                margin="dense"
                            />
                            <TextField
                                label="Texto/Caption (opcional)"
                                placeholder="Agrega un texto que se enviará junto con el documento"
                                value={caption}
                                onChange={(e) => setCaption(e.target.value)}
                                fullWidth
                                variant="outlined"
                                margin="dense"
                                multiline
                                rows={4}
                                helperText="El texto se enviará como caption del documento (como en WhatsApp)"
                            />
                            {open === "edit" && (
                                <Stack>
                                    <Typography variant="body1">
                                        [V2] {i18n.t("flowBuilder.doc.currentFile") || "Archivo actual"}: <b>{data.data.url ? data.data.url.split(/[/\\]/).pop() : ""}</b>
                                    </Typography>
                                    {data?.data?.url && (
                                        <div style={{ display: 'flex', justifyContent: 'center', marginTop: '10px', width: '100%', height: '300px' }}>
                                            <iframe title="Document Preview" src={getMediaUrl(data.data.url, companyId)} width="100%" height="100%" style={{border: 'none'}} />
                                        </div>
                                    )}
                                </Stack>
                            )}
                            {!loading && medias.length === 0 && ( /* Removed open !== "edit" */
                                <>
                                    <Button variant="contained" component="label">
                                        {i18n.t("flowBuilder.doc.upload") || "Enviar documento"}
                                        <input
                                            type="file"
                                            accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt"
                                            disabled={loading}
                                            hidden
                                            onChange={handleChangeMedias}
                                        />
                                    </Button>
                                    <Typography variant="caption" color="textSecondary">
                                        PDF, Office, TXT
                                    </Typography>
                                </>
                            )}
                            {medias.length > 0 && (
                                <Stack align="center">
                                    <Typography variant="body1" align="center">
                                        Archivo seleccionado: <b>{medias[0].name}</b>
                                    </Typography>
                                    {medias[0].type === "application/pdf" && (
                                        <div style={{ display: 'flex', justifyContent: 'center', marginTop: '10px' }}>
                                            <embed src={URL.createObjectURL(medias[0])} type="application/pdf" width="200px" height="200px" />
                                        </div>
                                    )}
                                </Stack>
                            )}
                            {loading && (
                                <>
                                    <Stack justifyContent={"center"} alignSelf={"center"}>
                                        <CircularProgress />
                                    </Stack>
                                </>
                            )}
                        </Stack>
                    </DialogContent>
                    <DialogActions>
                        {!loading && (
                            <>
                                <Button onClick={() => {
                                        handleClose();
                                        setMedias([]);
                                        setCaption("");
                                    }} 
                                    className={classes.secondaryButton}
                                >
                                    {i18n.t("common.cancel") || "Cancelar"}
                                </Button>
                                <Button 
                                    type="submit" 
                                    onClick={() => handleSaveContact()} 
                                    disabled={loading || (medias.length === 0 && open === "create")} 
                                    className={classes.applyButton}
                                >
                                    {`${labels.btn}`}
                                </Button>
                            </>
                        )}
                    </DialogActions>
                </Stack>
            </Dialog>
        </div>
    );
};

export default FlowBuilderAddDocModal;
